"use client";

import * as React from "react";
import * as Popover from "@radix-ui/react-popover";
import { Drawer } from "vaul";
import { useAppLauncherContext } from "../providers/app-launcher-provider";
import { useGlobalMenu } from "../queries/use-global-menu";
import type { GlobalMenuItem } from "../types";
import {
  getGlobalMenuItemStatus,
  getSortedGlobalMenuItems,
  resolveGlobalMenuItemAssetUrl,
} from "../menu";
import { BackArrowIcon, CloseIcon } from "./icons";
import { cn } from "../../../lib/utils";
import { TrackingBoundary } from "../../../lib/sentry/index";

export interface AppLauncherContentProps {
  align?: "start" | "center" | "end";
  sideOffset?: number;
  className?: string;
}

function isStakingItem(item: GlobalMenuItem) {
  return item.id === "cross-staking" || item.type === "cross-staking";
}

export function AppLauncherContent({
  align = "end",
  sideOffset = 8,
  className,
}: AppLauncherContentProps) {
  const { env, theme, isMobile, domain, close } =
    useAppLauncherContext();
  const { data } = useGlobalMenu(env);
  const menuItems = React.useMemo(
    () => getSortedGlobalMenuItems(data?.items ?? []),
    [data?.items],
  );

  const DRAG_THRESHOLD = 8;
  const pointerStart = React.useRef<{ x: number; y: number } | null>(null);

  const handlePointerDown = React.useCallback((e: React.PointerEvent) => {
    pointerStart.current = { x: e.clientX, y: e.clientY };
  }, []);

  const currentPathname =
    typeof window !== "undefined" ? window.location.pathname : "";

  const handleItemClick = React.useCallback(
    (e: React.MouseEvent, item: GlobalMenuItem) => {
      if (pointerStart.current) {
        const dx = e.clientX - pointerStart.current.x;
        const dy = e.clientY - pointerStart.current.y;
        pointerStart.current = null;
        if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) {
          return;
        }
      }

      const url = item.url[env] ?? item.url.production;
      const isExternal = isExternalLink(url, domain);

      if (!isExternal) {
        try {
          const urlPathname = new URL(url).pathname;
          const isActive =
            currentPathname === urlPathname ||
            currentPathname.startsWith(urlPathname + "/");
          if (isActive) {
            close();
            return;
          }
        } catch {}
      }

      if (isExternal) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        window.location.href = url;
      }

      close();
    },
    [env, domain, close, currentPathname],
  );

  const renderMenuItem = (item: GlobalMenuItem) => {
    const url = item.url[env] ?? item.url.production;
    const isExternal = isExternalLink(url, domain);
    const serviceStatus = getGlobalMenuItemStatus(item);
    const isEndingSoon = serviceStatus === "ending";
    const iconUrl = resolveGlobalMenuItemAssetUrl(item.iconUrl, env);
    let isActive = false;
    if (!isExternal) {
      try {
        const urlPathname = new URL(url).pathname;
        isActive =
          currentPathname === urlPathname ||
          currentPathname.startsWith(urlPathname + "/");
      } catch {
        isActive = false;
      }
    }

    return (
      <button
        key={item.id}
        type="button"
        className="al-grid-item"
        data-track="menu_item_click"
        data-track-id={item.id}
        data-active={isActive || undefined}
        data-service-status={serviceStatus}
        onPointerDown={handlePointerDown}
        onClick={(e) => handleItemClick(e, item)}
      >
        <div className="al-grid-item-left">
          <div className="al-grid-item-icon">
            <img
              src={iconUrl}
              alt={item.label}
              width={24}
              height={24}
              loading="lazy"
            />
            {item.isNew && (
              <span className="al-status-ribbon al-status-ribbon-new">N</span>
            )}
          </div>
          <span className="al-grid-item-text">
            <span className="al-grid-item-label">{item.label}</span>
            {isEndingSoon && (
              <span className="al-grid-item-status-text">(End Soon)</span>
            )}
            {item.badge && !isStakingItem(item) && (
              <span className="al-badge al-badge-list">{item.badge}</span>
            )}
          </span>
        </div>
      </button>
    );
  };

  const menuContent = (
    <div className="al-menu" data-theme={theme}>
      <div className="al-grid">{menuItems.map(renderMenuItem)}</div>
    </div>
  );

  if (isMobile) {
    return (
      <TrackingBoundary feature="app_launcher">
        <Drawer.Portal>
          <Drawer.Overlay className="al-drawer-overlay" />
          <Drawer.Content
            className={cn("al-drawer-content", className)}
            data-theme={theme}
          >
            <div className="al-drawer-header">
              <Drawer.Close className="al-drawer-close" data-track="close">
                <BackArrowIcon />
              </Drawer.Close>
              <Drawer.Title className="al-drawer-title">
                CROSS Services
              </Drawer.Title>
              <Drawer.Close className="al-drawer-close" data-track="close">
                <CloseIcon />
              </Drawer.Close>
            </div>
            <div className="al-drawer-body">{menuContent}</div>
          </Drawer.Content>
        </Drawer.Portal>
      </TrackingBoundary>
    );
  }

  return (
    <TrackingBoundary feature="app_launcher">
      <Popover.Portal>
        <Popover.Content
          align={align}
          sideOffset={sideOffset}
          className={cn("al-popover-content", className)}
          data-theme={theme}
        >
          {menuContent}
        </Popover.Content>
      </Popover.Portal>
    </TrackingBoundary>
  );
}

function isExternalLink(url: string, domain?: string): boolean {
  try {
    const linkHostname = new URL(url).hostname;
    if (typeof window === "undefined") return true;
    return linkHostname !== (domain ?? window.location.hostname);
  } catch {
    return false;
  }
}
